import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '../../utils/debounce';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Card,
    CardContent,
    Alert,
    InputAdornment,
    useMediaQuery,
    useTheme,
    tooltipClasses,
    TooltipProps,
} from '@mui/material';
import { Add, Edit, Delete, Search, Business } from '@mui/icons-material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import api from '../../api/axios';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

interface ComplaintDepartment {
    id: number;
    name: string;
    created_at: string;
}

const ComplaintDepartments = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();

    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [open, setOpen] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState<ComplaintDepartment | null>(null);
    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [departmentToDelete, setDepartmentToDelete] = useState<ComplaintDepartment | null>(null);

    // Fetch departments
    const { data, isLoading } = useQuery({
        queryKey: ['complaint-departments', pageModel.page, pageModel.pageSize, debouncedSearch],
        queryFn: async () => {
            const params: any = { page: pageModel.page + 1, per_page: pageModel.pageSize };
            if (debouncedSearch) params.search = debouncedSearch;
            const response = await api.get('/superadmin/complaint-departments', { params });
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    // Create/Update mutation
    const saveMutation = useMutation({
        mutationFn: async (departmentName: string) => {
            if (selectedDepartment) {
                return api.put(`/superadmin/complaint-departments/${selectedDepartment.id}`, { name: departmentName });
            }
            return api.post('/superadmin/complaint-departments', { name: departmentName });
        },
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['complaint-departments'] });
            handleClose();
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message);
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            return api.delete(`/superadmin/complaint-departments/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['complaint-departments'] });
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message);
        },
    });

    const handleOpen = (department?: ComplaintDepartment) => {
        if (department) {
            setSelectedDepartment(department);
            setName(department.name);
        } else {
            setSelectedDepartment(null);
            setName('');
        }
        setError('');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedDepartment(null);
        setName('');
        setError('');
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        saveMutation.mutate(name);
    };

    const handleDeleteClick = (department: ComplaintDepartment) => {
        setDepartmentToDelete(department);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (departmentToDelete) {
            deleteMutation.mutate(departmentToDelete.id);
        }
        setDeleteDialogOpen(false);
        setDepartmentToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setDepartmentToDelete(null);
    };

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Sr. No',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        { field: 'name', headerName: 'Department Name', flex: 1, minWidth: 200 },
        {
            field: 'created_at',
            headerName: 'Created',
            width: 120,
            valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A',
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'green' }}>
                        <BootstrapTooltip title="Edit" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleOpen(params.row)}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'red' }}>
                        <BootstrapTooltip title="Delete" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleDeleteClick(params.row)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                </Box>
            ),
        },
    ];

    // Mobile columns - fewer columns for smaller screens
    const mobileColumns: GridColDef[] = [
        { field: 'name', headerName: 'Department Name', flex: 1 },
        {
            field: 'actions',
            headerName: '',
            width: 80,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <IconButton size="small" onClick={() => handleOpen(params.row)}>
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    const rows = data?.data?.data || data?.data || [];
    const rowCount = data?.data?.total || data?.total || 0;

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h2">Request Departments</Typography>
            </Box>

            {/* Search */}
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <TextField
                        sx={{ width: '30%', borderRadius: 1 }}
                        size="small"
                        placeholder="Search departments..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button variant="contained" startIcon={<Business />} onClick={() => handleOpen()}>
                        Add Department
                    </Button>
                </CardContent>
            </Card>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* DataGrid */}
            <Card>
                <DataGrid
                    rows={rows}
                    columns={isMobile ? mobileColumns : columns}
                    loading={isLoading}
                    paginationModel={pageModel}
                    onPaginationModelChange={setPageModel}
                    pageSizeOptions={[10, 25, 50]}
                    paginationMode="server"
                    rowCount={rowCount}
                    autoHeight
                    disableRowSelectionOnClick
                    sx={{ border: 0 }}
                />
            </Card>

            {/* Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle fontSize={22}>{selectedDepartment ? 'Edit Department' : 'Add Department'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        <TextField
                            label="Department Name"
                            fullWidth
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                        <Button type="submit" color="success" variant="contained" disabled={saveMutation.isPending}>
                            {saveMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
                <Box sx={{ bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #fecaca' }}>
                    <Box sx={{ bgcolor: '#dc2626', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>Are you sure you want to delete <strong>"{departmentToDelete?.name}"</strong>?</Typography>
                    <Alert severity="warning" sx={{ bgcolor: '#fef3c7', border: '1px solid #fcd34d', '& .MuiAlert-icon': { color: '#d97706' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Warning: You will not be able to revert this action!</Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={handleDeleteCancel} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}
                        startIcon={<Delete />} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ComplaintDepartments;
